// ThreadRow — a thread is a QUEUE item (spec §1 / §3 / §5).
//
// Discrete: opens, closes, assignable, resolvable. This is the only resolvable
// object, so it is the only one that gets a <SituationCard> (a work row) and an
// <AwaitingUsBadge>. The body is composed server-side (`summary` / `says`) — never
// print raw fields; <Sentence> renders the summary verbatim. Client-facing is a
// coloured left border + label. urgency maps to the warm/cool tint (the only
// place a thread's urgency becomes a visual weight).
//
// Task comments are DELIBERATELY EXCLUDED — this row never renders a task thread;
// the rail never offers a task scope (spec §1).
import React from "react";
import SituationCard from "../kit/SituationCard";
import Sentence from "../kit/Sentence";
import AwaitingUsBadge from "./AwaitingUsBadge";
import { formatWhen } from "../../shared/format";

// urgency → SituationCard severity → tint (warm = leaking/red, cool = calm/gray).
// A thread the client is waiting on (awaiting_us) reads warm regardless of its
// urgency field — "they are waiting on you" is the loudest thing on the row.
const severityFor = (thread) =>
  thread?.awaiting_us ? "critical" : thread?.urgency || "watch";

/**
 * @param {object} props
 * @param {object} props.thread   An fn_conversation_threads item (see api/conversations.js).
 * @param {boolean} props.selected
 * @param {(id: string) => void} props.onSelect
 */
export default function ThreadRow({ thread, selected, onSelect }) {
  const t = thread || {};
  return (
    <button
      type="button"
      data-testid="thread-row"
      data-thread-id={t.id}
      aria-current={selected ? "page" : undefined}
      onClick={() => onSelect(t.id)}
      className={`w-full rounded-xl border text-left transition-colors ${
        selected
          ? "ring-2 ring-[#32B1FF] border-transparent"
          : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
      } ${t.client_facing ? "border-l-4 border-l-[#32B1FF]" : ""}`}
    >
      <SituationCard
        severity={severityFor(t)}
        dataAttrs={{ "data-thread-id": t.id }}
        className="!border-0 !ring-0"
      >
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {t.client_facing && (
                <span className="shrink-0 rounded bg-[#32B1FF]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#32B1FF]">
                  Client
                </span>
              )}
              <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t.subject || "(no subject)"}
              </span>
            </div>
            {t.summary && (
              <Sentence says={t.summary} tint={t.awaiting_us ? "warm" : "cool"} size="sm" className="mt-1" />
            )}
            <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-400">
              {t.client && <span className="truncate">{t.client}</span>}
              {t.assigned && <span className="truncate">· {t.assigned}</span>}
              {t.last_at && <span className="ml-auto tabular-nums">{formatWhen(t.last_at)}</span>}
            </div>
          </div>
          {t.awaiting_us && (
            <AwaitingUsBadge className="shrink-0" count={typeof t.messages === "number" ? t.messages : undefined} />
          )}
        </div>
      </SituationCard>
    </button>
  );
}
